import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import katex from "katex";
// @ts-ignore
import { InlineMath, BlockMath } from 'react-katex';
// @ts-ignore - Ignoring type issues with rehype plugins
import rehypeKatex from 'rehype-katex';
// @ts-ignore - Ignoring type issues with rehype plugins
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import "katex/dist/katex.min.css";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { Grid } from '@mui/material';
import BackArrow from '../../assets/images/BackArrow.svg';
import CheckMarkGreen from '../../assets/images/CheckMarkGreen.svg';
import Cancel from '../../assets/images/Cancel.svg';
import AI from '../../assets/images/AI.svg';
// @ts-ignore - Ignoring type issues with react-markdown
import ReactMarkdown from 'react-markdown';
// @ts-ignore - Ignoring type issues with remark plugins
import remarkGfm from 'remark-gfm';
// @ts-ignore - Ignoring type issues with remark plugins
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import { getOpportunity, updateOpportunityStatus, generateEmail } from '../../api/opportunities';
import ReferencesTable from '../../components/ReferencesTable';
import type { Reference } from '../../components/ReferencesTable';
import CitationPanel from '../../components/citationPanel/CitationPanel';

interface Opportunity {
  id: number;
  opportunity_name: string;
  client_name: string;
  status: string;
  value: string;
  created_date: string;
  reasoning?: string;
  answer?: string;
  references: Reference[];
  email_body: string;
  mailto_url: string;
  metadata_?: {
    code_output?: string;
    code_snippet?: string;
    [key: string]: any;
  };
  scenario?: {
    scenario_id: number;
    category: string;
    scope: string;
    [key: string]: any;
  };
}

// Using Reference interface from ReferencesTable component

const OpportunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Citation handling
  const [isCitationOpen, setIsCitationOpen] = useState(false);
  const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
  const [isNextStepsModalOpen, setIsNextStepsModalOpen] = useState(false);
  // for email generation
  const [streamingEmailContent, setStreamingEmailContent] = useState('');
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOpportunity = async () => {
      try {
        if (!id) {
          console.error('No opportunity ID provided');
          return;
        }
        console.log('Starting to fetch opportunity:', id);
        const data = await getOpportunity(parseInt(id));
        console.log('Received opportunity data:', data);
        setOpportunity(data);
      } catch (err) {
        console.error('Error fetching opportunity:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch opportunity');
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunity();
  }, [id]);

  console.log('Current state:', { loading, error, opportunity });

  if (loading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  if (error || !opportunity) {
    return <Box sx={{ p: 3 }}>Error: {error || 'Opportunity not found'}</Box>;
  }

  const handleApprove = async () => {
    try {
      await updateOpportunityStatus(opportunity.id, 'approved');
      // Dispatch event to update notification count
      window.dispatchEvent(new Event('opportunityStatusChange'));
      navigate('/homepage/opportunities');
    } catch (err) {
      console.error('Error approving opportunity:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve opportunity');
    }
  };

  const handleReject = async () => {
    try {
      await updateOpportunityStatus(opportunity.id, 'rejected');
      // Dispatch event to update notification count
      window.dispatchEvent(new Event('opportunityStatusChange'));
      navigate('/homepage/opportunities');
    } catch (err) {
      console.error('Error rejecting opportunity:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject opportunity');
    }
  };

  const fetchStreamingEmail = async () => {
    setIsEmailLoading(true);
    setEmailError(null);
    setStreamingEmailContent('');
    
    try {
      await generateEmail({
        opportunity_name: opportunity?.opportunity_name,
        client_name: opportunity?.client_name,
        status: opportunity?.status,
        value: opportunity?.value,
        created_date: opportunity?.created_date,
        reasoning: opportunity?.reasoning,
        answer: opportunity?.answer
      }, (chunk) => {
        setStreamingEmailContent(prev => prev + chunk);
      });
    } catch (err) {
      console.error('Error generating email:', err);
      setEmailError(err instanceof Error ? err.message : 'Failed to generate email');
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleGetNextSteps = () => {
    setIsNextStepsModalOpen(true);
    fetchStreamingEmail();
  };

  const toggleCitation = (reference?: Reference) => {
    if (reference) {
      setSelectedReference(reference);
      setIsCitationOpen(true);
    } else {
      setIsCitationOpen(!isCitationOpen);
      if (!isCitationOpen) {
        setSelectedReference(null);
      }
    }
  };

  const ActionButtons = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      gap={2}
      mb={3}
    >
      <Button
        variant="outlined"
        onClick={handleGetNextSteps}
        sx={{
          fontSize: "0.875rem",
          width: 220,
          height: 38,
          borderRadius: "3px",
          background: "#FFF",
          color: "#8950FC",
          cursor: "pointer",
          borderColor: "#8950FC",
          textTransform: 'none',
          '&:hover': {
            borderColor: "#8950FC",
            background: "#FFF",
          }
        }}
        startIcon={<img src={AI} alt="AI" style={{ width: 20, height: 20 }} />}
      >
        Get next steps
      </Button>
      <Button
        variant="outlined"
        onClick={handleApprove}
        sx={{
          fontSize: "0.875rem",
          width: 220,
          height: 38,
          borderRadius: "3px",
          background: "#FFF",
          color: "#8950FC",
          cursor: "pointer",
          borderColor: "#8950FC",
          textTransform: 'none',
          '&:hover': {
            borderColor: "#8950FC",
            background: "#FFF",
          }
        }}
        startIcon={<img src={CheckMarkGreen} alt="Approve" style={{ width: 20, height: 20 }} />}
      >
        Approve
      </Button>
      <Button
        variant="contained"
        onClick={handleReject}
        sx={{
          fontSize: "0.875rem",
          width: 200,
          height: 38,
          color: "#ffffff",
          backgroundColor: "#8950FC",
          cursor: "pointer",
          textTransform: 'none',
          '&:hover': {
            backgroundColor: "#7339FB",
          }
        }}
        startIcon={<img src={Cancel} alt="Reject" style={{ width: 20, height: 20 }} />}
      >
        Reject
      </Button>
    </Box>
  );

  const openCitationByMetadata = (fileId: number, pageLabel: number) => {
    const pageNumber = pageLabel + 1;
    const matchedReference =
      opportunity.references.find(
        (ref) =>
          Number(ref.fileId) === fileId &&
          (Number(ref.pageNumber) === pageLabel || Number(ref.pageNumber) === pageNumber)
      );

    const referenceToOpen = matchedReference
      ? {
          ...matchedReference,
          pageNumber
        }
      : {
          id: Date.now(),
          fileName: `File ${fileId}`,
          pageNumber,
          fileId: fileId.toString(),
          text: ''
        };

    setSelectedReference(referenceToOpen);
    setIsCitationOpen(true);
  };

  const renderContent = (rawText: string): React.ReactNode => {
    // Process LaTeX expressions manually
    const processedText = rawText
      // Replace block LaTeX expressions (\[ ... \])
      .replace(/\\\[(.*?)\\\]/gs, (match, formula) => {
        return `<div class="katex-block">${katex.renderToString(formula, { displayMode: true, throwOnError: false })}</div>`;
      })
      // Replace inline LaTeX expressions (\( ... \))
      .replace(/\\\((.*?)\\\)/gs, (match, formula) => {
        return katex.renderToString(formula, { displayMode: false, throwOnError: false });
      })
      // Replace metadata citations with clickable inline links
      .replace(/\[([^\]]*file_id\s*=\s*\d+[^\]]*page_label\s*=\s*\d+[^\]]*)\]/gi, (match, citationText) => {
        const fileIdMatch = citationText.match(/file_id\s*=\s*(\d+)/i);
        const pageLabelMatch = citationText.match(/page_label\s*=\s*(\d+)/i);
        if (!fileIdMatch || !pageLabelMatch) {
          return match;
        }
        const fileId = Number(fileIdMatch[1]);
        const pageLabel = Number(pageLabelMatch[1]);
        const pageNumber = pageLabel + 1;
        const clickableLabel = `Link to Contract Reference, Page ${pageNumber}`;
        return `[${clickableLabel}](/citation-reference?file_id=${fileId}&page_label=${pageLabel})`;
      });
    
    return (
      <div>
        <ReactMarkdown
          remarkPlugins={[remarkGfm] as any[]}
          rehypePlugins={[rehypeRaw] as any[]}
          components={{
            // Custom handling for math elements if needed
            p: ({ children }: React.PropsWithChildren<{}>) => <p style={{ marginBottom: '1rem' }}>{children}</p>,
            h3: ({ children }: React.PropsWithChildren<{}>) => <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>{children}</Typography>,
            table: ({ children }: React.PropsWithChildren<{}>) => (
              <div style={{ overflowX: 'auto', margin: '1rem 0' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>{children}</table>
              </div>
            ),
            th: ({ children }: React.PropsWithChildren<{}>) => (
              <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#f2f2f2', textAlign: 'left' }}>{children}</th>
            ),
            td: ({ children }: React.PropsWithChildren<{}>) => (
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{children}</td>
            ),
            a: ({ href, children, ...props }: any) => {
              const isCitationLink = typeof href === 'string' && href.startsWith('/citation-reference?');
              if (!isCitationLink) {
                return (
                  <a href={href} {...props}>
                    {children}
                  </a>
                );
              }

              return (
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    const fileIdMatch = href.match(/file_id=(\d+)/);
                    const pageLabelMatch = href.match(/page_label=(\d+)/);
                    if (fileIdMatch && pageLabelMatch) {
                      openCitationByMetadata(Number(fileIdMatch[1]), Number(pageLabelMatch[1]));
                    }
                  }}
                  style={{ color: '#8950FC', textDecoration: 'underline', cursor: 'pointer' }}
                  {...props}
                >
                  {children}
                </a>
              );
            },
          }}
        >
          {processedText}
        </ReactMarkdown>
      </div>
    );
  };

  const handleEmailOpen = () => {
    if (!opportunity?.mailto_url) return;
    
    // Try to open the mailto link
    const mailtoWindow = window.open(opportunity.mailto_url);
    
    // If window.open returns null, the popup was blocked or failed
    if (!mailtoWindow) {
      // Fallback: create a temporary link and click it
      const link = document.createElement('a');
      link.href = opportunity.mailto_url;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Grid
        sx={{
          flex: 1,
          mt: 8,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
          borderRadius: "8px",
          width: "100%",
          pb: 4
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <Button
            onClick={() => navigate('/homepage/opportunities')}
            sx={{ 
              mr: 2,
              color: '#8950FC',
              textTransform: 'none',
              '&:hover': {
                background: 'transparent',
                color: '#7339FB'
              }
            }}
            startIcon={<img src={BackArrow} alt="Back" />}
          >
            Back
          </Button>
          <Typography sx={{ fontSize: "1.15rem" }}>
            Opportunity Details
          </Typography>
        </Box>
        <Divider sx={{ width: "100%", mb: 2 }} />
        
        <Box sx={{ px: 3, width: '100%' }}>
          <ActionButtons />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Opportunity Name
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2, 
                      cursor: 'pointer',
                      color: '#8950FC',
                      '&:hover': {
                        textDecoration: 'underline'
                      } 
                    }}
                    onClick={() => opportunity?.scenario?.scenario_id && 
                      window.open(`/homepage/scenarios/${opportunity.scenario.scenario_id}`, '_blank')}
                  >
                    {opportunity.opportunity_name}
                  </Typography>

                  {opportunity?.scenario?.category && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary">
                        Category
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {opportunity.scenario.category}
                      </Typography>
                    </>
                  )}

                  {opportunity?.scenario?.scope && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary">
                        Scope
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {opportunity.scenario.scope}
                      </Typography>
                    </>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Client
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {opportunity.client_name}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {opportunity.status}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Financial Details
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Value
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {opportunity.value}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {opportunity.created_date}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {opportunity.answer && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Analysis
                  </Typography>
                  <Box sx={{ 
                    mt: 2, 
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      marginBottom: 2,
                    },
                    '& th, & td': {
                      border: '1px solid rgba(224, 224, 224, 1)',
                      padding: '8px',
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& h3': {
                      color: '#1976d2',
                      marginTop: 3,
                      marginBottom: 2,
                    },
                    '& p': {
                      marginBottom: 2,
                    }
                  }}>
                    {renderContent(opportunity.answer)}
                  </Box>
                </Paper>
              </Grid>
            )}
            {(opportunity?.metadata_?.code_output || opportunity?.metadata_?.code_snippet) && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Detailed Reasoning
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {opportunity?.metadata_?.code_snippet && (
                      <Accordion defaultExpanded={false}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="code-snippet-content"
                          id="code-snippet-header"
                        >
                          <Typography>View Code Snippet</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box 
                            component="pre" 
                            sx={{
                              backgroundColor: '#f5f5f5',
                              padding: 2,
                              borderRadius: 1,
                              overflowX: 'auto',
                              fontSize: '0.875rem',
                              fontFamily: '"Roboto Mono", monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            {opportunity.metadata_.code_snippet}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                    {opportunity?.metadata_?.code_output && (
                      <Accordion defaultExpanded={false}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="code-output-content"
                          id="code-output-header"
                        >
                          <Typography>View Code Output</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box 
                            component="pre" 
                            sx={{
                              backgroundColor: '#f5f5f5',
                              padding: 2,
                              borderRadius: 1,
                              overflowX: 'auto',
                              fontSize: '0.875rem',
                              fontFamily: '"Roboto Mono", monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            {opportunity.metadata_.code_output}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <ReferencesTable references={opportunity.references} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <ActionButtons />
          </Box>
        </Box>
      </Grid>

      {/* Inline citation links in analysis open this panel */}
      <CitationPanel
        open={isCitationOpen}
        onClose={() => setIsCitationOpen(false)}
        selectedReference={selectedReference}
      />
      <Modal
        open={isNextStepsModalOpen}
        onClose={() => setIsNextStepsModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60%',
          maxWidth: '800px',
          bgcolor: 'background.paper',
          borderRadius: '8px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          p: 4
          // position: 'relative'
        }}>
          <IconButton
            onClick={() => setIsNextStepsModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#718096',
              '&:hover': {
                color: '#4A5568',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography 
            id="modal-modal-title" 
            variant="h5" 
            component="h2"
            sx={{ 
              fontWeight: 600,
              color: '#2D3748',
              mb: 3,
              pr: 4 // Add padding to prevent text from going under close button
            }}
          >
            Reach out to the supplier
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2,
              color: '#4A5568',
              fontWeight: 500 
            }}
          >
            Below is a drafted email explaining the issue
          </Typography>
          <Box 
            sx={{ 
              p: 3,
              backgroundColor: '#F7FAFC',
              borderRadius: '4px',
              border: '1px solid #E2E8F0',
              mb: 3,
              whiteSpace: 'pre-wrap',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: '0.875rem',
              lineHeight: 1.6,
              color: '#2D3748',
              minHeight: '200px',
              maxHeight: '400px',
              overflowY: 'auto'
            }}
          >
            {isEmailLoading && (
              <Typography sx={{ color: '#718096' }}>
                Generating email...
              </Typography>
            )}
            
            {emailError && (
              <Typography sx={{ color: '#E53E3E' }}>
                Error: {emailError}
              </Typography>
            )}
            
            {
              streamingEmailContent || 
              // opportunity?.email_body || 
              'No email content available'
            }
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleEmailOpen}
              disabled={!opportunity?.mailto_url}
              sx={{
                fontSize: "0.875rem",
                minWidth: 150,
                height: 38,
                color: "#ffffff",
                backgroundColor: "#8950FC",
                cursor: "pointer",
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: "#7339FB",
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E2E8F0',
                  color: '#A0AEC0'
                },
                borderRadius: '4px'
              }}
            >
              Open in Email Application
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default OpportunityDetail;
